import React, { useEffect, useState } from "react";
import { PopupModal, InlineWidget } from "react-calendly";
import { Calendar, ArrowRight } from "lucide-react";

/**
 * Calendly booking integration for Relatable Studio.
 *
 *  Event type embedded: 30-min project scoping call
 *  URL:                 https://calendly.com/nexa-relatable/30min
 *
 * Notifications: rudhravishnu@relatable.studio is added under Calendly's
 * "Additional email notifications" for this event type in the Calendly UI —
 * no backend forwarding is required.
 *
 * Exports:
 *   <BookACallFab />     Floating fixed pill (bottom-right) → popup modal.
 *                        Mounted globally in App.js Shell. Hides on /admin*.
 *   <BookACallInline />  In-page embed for /contact page.
 *
 * Prefill: both variants read `relatable_contact_prefill` from localStorage
 * so if a visitor started the contact form, the booking widget auto-populates
 * name + email. Contact.jsx writes those keys on every keystroke.
 */

const CAL_URL = "https://calendly.com/nexa-relatable/30min";

// Calendly page theming — matches Relatable brand palette exactly.
// Values MUST be raw hex WITHOUT the "#" — that's the Calendly widget contract.
const PAGE_SETTINGS = {
  backgroundColor: "0a0a0a",       // ink
  primaryColor: "0C14E4",          // ultrasonic blue
  textColor: "FFFFFF",             // bone
  hideEventTypeDetails: false,
  hideLandingPageDetails: false,
  hideGdprBanner: true,
};

// Utility — read prefill safely (never throw during render).
function readPrefill() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem("relatable_contact_prefill");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      name: (parsed.name || "").slice(0, 120),
      email: (parsed.email || "").slice(0, 200),
    };
  } catch {
    return {};
  }
}

// Utility — read UTM attribution for Calendly custom-answer piping.
function readUtm() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem("relatable_utm");
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

// -----------------------------------------------------------------------------
// Floating pill — persistent site-wide entry to booking.
// -----------------------------------------------------------------------------

export function BookACallFab() {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({});
  const [utm, setUtm] = useState({});
  const [visible, setVisible] = useState(false);

  // Delay the fab entrance so it doesn't compete with the hero reel / preloader.
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 2200);
    return () => clearTimeout(t);
  }, []);

  // Hide on admin routes — booking is for public visitors only.
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const check = () => setHidden(window.location.pathname.startsWith("/admin"));
    check();
    window.addEventListener("popstate", check);
    return () => window.removeEventListener("popstate", check);
  }, []);

  const handleOpen = () => {
    setPrefill(readPrefill());
    setUtm(readUtm());
    setOpen(true);
  };

  if (hidden) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Book a call with Relatable Studio"
        data-testid="book-a-call-fab"
        onClick={handleOpen}
        className={`fixed z-40 bottom-6 right-6 md:bottom-8 md:right-8 group inline-flex items-center gap-2 md:gap-3 pl-4 pr-5 md:pl-5 md:pr-6 py-3 md:py-3.5 bg-relatable text-bone eyebrow text-[11px] md:text-xs shadow-[0_10px_40px_-8px_rgba(12,20,228,0.55)] border border-relatable/60 hover:bg-bone hover:text-ink hover:border-bone transition-all duration-300 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
        }`}
      >
        <Calendar className="w-4 h-4 md:w-[18px] md:h-[18px] shrink-0" strokeWidth={1.6} />
        <span className="tracking-[0.2em]">BOOK A CALL</span>
        <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" strokeWidth={1.6} />
      </button>

      {typeof document !== "undefined" && (
        <PopupModal
          url={CAL_URL}
          open={open}
          rootElement={document.body}
          onModalClose={() => setOpen(false)}
          pageSettings={PAGE_SETTINGS}
          prefill={{
            name: prefill.name || "",
            email: prefill.email || "",
            customAnswers: {
              a1: utm.utm_campaign || utm.utm_source || "website",
            },
          }}
          utm={{
            utmSource: utm.utm_source || "website",
            utmMedium: utm.utm_medium || "book_a_call_fab",
            utmCampaign: utm.utm_campaign || "relatable_book_call",
          }}
        />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Inline widget — sits on /contact underneath the form.
// -----------------------------------------------------------------------------

export function BookACallInline() {
  const [prefill, setPrefill] = useState({});
  const [utm, setUtm] = useState({});

  // Refresh prefill whenever the tab is focused / storage changes — so if the
  // visitor types in the contact form above, the Calendly iframe reflects it
  // on the next mount. (React re-renders won't remount the iframe.)
  useEffect(() => {
    const sync = () => {
      setPrefill(readPrefill());
      setUtm(readUtm());
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return (
    <div
      data-testid="book-a-call-inline"
      className="border border-line bg-[#0d0d0d] p-3 md:p-4"
    >
      <InlineWidget
        url={CAL_URL}
        pageSettings={PAGE_SETTINGS}
        prefill={{
          name: prefill.name || "",
          email: prefill.email || "",
        }}
        utm={{
          utmSource: utm.utm_source || "contact_page",
          utmMedium: utm.utm_medium || "inline_widget",
          utmCampaign: utm.utm_campaign || "relatable_book_call",
        }}
        styles={{ height: "720px", width: "100%" }}
      />
    </div>
  );
}

// Named default export intentionally omitted — importers use the named exports.
export default BookACallFab;
